import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';

interface FadeInProps {
  /** Delay in seconds before the animation starts. */
  delay?: number;
  /** Vertical offset (px) to travel from. Defaults to 8. Zeroed when prefers-reduced-motion. */
  y?: number;
  /** Animation duration in seconds. Defaults to 0.25. */
  duration?: number;
  children: React.ReactNode;
  /** Forwarded to the underlying div via style. */
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Lightweight fade-in wrapper. Imports framer-motion so consuming screens
 * don't have to. All motion parameters are neutralised when the OS reports
 * prefers-reduced-motion: opacity still transitions but at zero duration
 * and zero displacement so the change feels instantaneous.
 *
 * Only this component (and StepTransition) import framer-motion directly.
 */
const FadeIn: React.FC<FadeInProps> = ({
  delay = 0,
  y = 8,
  duration = 0.25,
  children,
  style,
  className,
}) => {
  const reducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={{ opacity: 0, y: reducedMotion ? 0 : y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reducedMotion ? 0 : duration,
        delay: reducedMotion ? 0 : delay,
        ease: 'easeOut',
      }}
      style={style}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default FadeIn;
